import { motion } from "framer-motion";
import { LandPlot, Building2, Home, Store, Sprout, ArrowUpRight } from "lucide-react";
import { WhatsAppIcon } from "./Logo";
import { SERVICES, waLink } from "../lib/brand";

const ICONS = {
  plot: LandPlot,
  builder_floor: Building2,
  farmhouse: Home,
  commercial: Store,
  agriculture: Sprout,
};

const row = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Services = () => {
  return (
    <section id="services" className="relative py-16 sm:py-24 lg:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20">
          {/* Left — sticky intro */}
          <div className="lg:sticky lg:top-28 self-start">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#c5a059]" />
              <span className="text-xs tracking-[0.34em] uppercase text-[#9a7b38] font-semibold">
                What We Deal In
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#131311] leading-[1]">
              Five ways we help you <span className="italic text-gold-gradient">grow your wealth.</span>
            </h2>
            <p className="mt-6 max-w-md text-[#52514c] leading-relaxed">
              Whether you're buying your first plot, selling a builder floor or investing in
              agricultural land, we handle every category across Hisar with the same
              transparency and care.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#f4efdf] border border-[#e7e3d7] px-5 py-3 text-sm font-semibold text-[#9a7b38]">
              Sale · Purchase · Rent
            </div>
          </div>

          {/* Right — offerings list */}
          <div className="divide-y divide-[#e7e3d7]">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.id] || LandPlot;
              return (
                <motion.div
                  key={s.id}
                  custom={i}
                  variants={row}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  className="group flex gap-5 lg:gap-7 py-7 lg:py-8"
                  data-testid={`service-card-${s.id}`}
                >
                  <div className="shrink-0">
                    <div className="h-14 w-14 rounded-2xl bg-[#131311] grid place-items-center text-[#f0d98a] transition-transform duration-500 group-hover:scale-105 group-hover:bg-[#9a7b38] group-hover:text-white">
                      <Icon className="h-6 w-6" strokeWidth={1.6} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="text-[11px] uppercase tracking-[0.16em] text-[#b3a988] font-semibold">
                          {s.n}
                        </span>
                        <h3 className="font-serif text-2xl lg:text-3xl font-semibold text-[#131311] leading-tight">
                          {s.title}
                        </h3>
                      </div>
                      <span className="hidden sm:inline-block shrink-0 rounded-full border border-[#e0dccf] px-3 py-1 text-[11px] font-semibold text-[#9a7b38]">
                        {s.tag}
                      </span>
                    </div>
                    <p className="mt-2 text-[15px] text-[#52514c] leading-relaxed">
                      {s.desc}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {s.types.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-[#f4efdf] px-3 py-1 text-[11px] font-medium text-[#6b675c]"
                        >
                          {t}
                        </span>
                      ))}
                      <a
                        href={waLink(
                          `Hello Laxmi Jiwan Properties, I'm interested in "${s.title}" in Hisar. Please share available options.`
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[#131311] lj-underline-link"
                        data-testid={`service-inquire-btn-${s.id}`}
                      >
                        <WhatsAppIcon className="h-4 w-4" /> Enquire
                        <ArrowUpRight className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
